import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, of, forkJoin } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DocumentService } from '../document/document.service';

export interface CommandeFournisseur {
    id?: number;
    reference: string;
    clientName: string;
    date: string;
    revenue: string;
    matricule: string;
    status: string;
    email?: string;
    phoneNumbers?: string[];
    station_id?: number | null;
    user_id?: number | null;
    fullData?: any;
}

@Injectable({
    providedIn: 'root'
})
export class AchatService {
    private apiBase = `${environment.document_url}api/documents/statut`;
    private stationId = 0;

    private typeMap: { [key: string]: string } = {
        'commande-fournisseur': '12',
        'bon-reception': '13',
        'facture-fournisseur': '14'
    };

    private statusMap: { [key: string]: string } = {
        'Tous': '0',
        'Brouillon': '1',
        'Crée': '9',
        'Valide': '10',
        'Annulé': '6',
        'Reçu': '14',
        'Payé': '5',
        'Non payé': '12',
        'Partiellement payé': '15',
        'Non utilisé': '19',
        'Partiellement utilisé': '20',
        'Utilisé': '18'
    };

    /** total_items de l'API GET api/documents/statut/{statusId}/{typeId} par statut (facture-fournisseur). */
    private factureFournisseurTotalItemsByStatus: Record<string, number> = {};

    /** total_items par statut pour RETOUR FOURNISSEUR (résolu via /api/type-documents). */
    private retourFournisseurTotalItemsByStatus: Record<string, number> = {};

    /**
     * Types achat affichés depuis la fiche fournisseur (API filtrée fournisseur).
     * Tant qu'actif, les rechargements globaux (loadAllData / liste Achat) ne réécrasent pas ce flux
     * — évite une course où la liste « toutes commandes » écrase les commandes du fournisseur.
     */
    private supplierDocumentScopeActive = new Set<string>();

    /** À appeler depuis Shared (fiche fournisseur) au montage / démontage. */
    setSupplierDocumentScope(type: string, active: boolean): void {
        if (active) {
            this.supplierDocumentScopeActive.add(type);
            /** Évite d’afficher la liste globale déjà en mémoire (loadAllData) avant la réponse API filtrée fournisseur. */
            this.replaceSubjectDataForType(type, []);
        } else {
            this.supplierDocumentScopeActive.delete(type);
        }
    }

    /** Écrit directement dans le BehaviorSubject (contourne le garde updateData). */
    private replaceSubjectDataForType(type: string, data: CommandeFournisseur[]): void {
        switch (type) {
            case 'commande-fournisseur':
                this.commandeFournisseurTableData.next(data);
                break;
            case 'bon-reception':
                this.bonReceptionTableData.next(data);
                break;
            case 'facture-fournisseur':
                this.factureFournisseurTableData.next(data);
                break;
            case 'retour-fournisseur':
                this.retourFournisseurTableData.next(data);
                break;
            case 'avoir-fournisseur':
                this.avoirFournisseurTableData.next(data);
                break;
        }
    }

    // BehaviorSubjects
    private commandeFournisseurTableData = new BehaviorSubject<CommandeFournisseur[]>([]);
    private bonReceptionTableData = new BehaviorSubject<CommandeFournisseur[]>([]);
    private factureFournisseurTableData = new BehaviorSubject<CommandeFournisseur[]>([]);
    private retourFournisseurTableData = new BehaviorSubject<CommandeFournisseur[]>([]);
    private avoirFournisseurTableData = new BehaviorSubject<CommandeFournisseur[]>([]);
    /** id type « AVOIR FOURNISSEUR » depuis /api/type-documents */
    private avoirFournisseurTypeDocumentId: string | null = null;
    /** id type document « RETOUR FOURNISSEUR » depuis /api/type-documents */
    private retourFournisseurTypeDocumentId: string | null = null;

    // Observables
    commandeFournisseurTableData$ = this.commandeFournisseurTableData.asObservable();
    bonReceptionTableData$ = this.bonReceptionTableData.asObservable();
    factureFournisseurTableData$ = this.factureFournisseurTableData.asObservable();
    retourFournisseurTableData$ = this.retourFournisseurTableData.asObservable();
    avoirFournisseurTableData$ = this.avoirFournisseurTableData.asObservable();

    constructor(
        private http: HttpClient,
        private documentService: DocumentService
    ) {
        this.loadUserData();
        this.loadAllData();
    }

    private loadUserData(): void {
        try {
            const userJson = localStorage.getItem('currentUser');
            if (userJson) {
                const user = JSON.parse(userJson);
                const station = user?.stations?.[0];
                this.stationId = station?.id ? Number(station.id) : 0;
            }
        } catch (error) {
            this.stationId = 0;
        }
    }

    private loadAllData() {
        this.loadByTypeAndStatus('commande-fournisseur', 'Tous');
        this.loadByTypeAndStatus('bon-reception', 'Tous');
        this.loadByTypeAndStatus('facture-fournisseur', 'Tous');
        this.loadByTypeAndStatus('retour-fournisseur', 'Tous');
    }

    loadByTypeAndStatus(type: string, status: string) {
        if (type === 'avoir-fournisseur' && !this.avoirFournisseurTypeDocumentId) {
            this.resolveAvoirFournisseurTypeDocumentId().pipe(
                mergeMap((id) => {
                    if (!id) {
                        this.updateData('avoir-fournisseur', []);
                        return EMPTY;
                    }
                    this.avoirFournisseurTypeDocumentId = String(id);
                    this.loadByTypeAndStatus('avoir-fournisseur', status);
                    return EMPTY;
                }),
                catchError(() => {
                    this.updateData('avoir-fournisseur', []);
                    return EMPTY;
                })
            ).subscribe();
            return;
        }

        if (type === 'retour-fournisseur' && !this.retourFournisseurTypeDocumentId) {
            this.resolveRetourFournisseurTypeDocumentId().pipe(
                mergeMap((id) => {
                    if (!id) {
                        this.updateData('retour-fournisseur', []);
                        return EMPTY;
                    }
                    this.retourFournisseurTypeDocumentId = String(id);
                    this.loadByTypeAndStatus('retour-fournisseur', status);
                    return EMPTY;
                }),
                catchError(() => {
                    this.updateData('retour-fournisseur', []);
                    return EMPTY;
                })
            ).subscribe();
            return;
        }

        const typeId = this.getTypeIdForLoad(type);
        if (!typeId) {
            this.updateData(type, []);
            return;
        }

        if (status === 'Tous') {
            this.loadAllStatusesForType(type);
            return;
        }

        const statusId = this.statusMap[status] ?? '0';
        const params = new HttpParams()
            .set('page', '1')
            .set('limit', '50')
            .set('station_id', this.stationId.toString());

        const url = `${this.apiBase}/${statusId}/${typeId}`;

        this.http.get<any>(url, { params }).pipe(
            tap(response => {
                const docs = response?.data?.documents || [];
                if (type === 'facture-fournisseur') {
                    const totalItems = response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                    this.factureFournisseurTotalItemsByStatus[status] = Number(totalItems) || 0;
                }
                if (type === 'retour-fournisseur') {
                    const totalItems = response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                    this.retourFournisseurTotalItemsByStatus[status] = Number(totalItems) || 0;
                }
            }),
            map(response => (response?.data?.documents || []).map(item => this.normalizeItem(item, status))),
            tap(data => this.updateData(type, data)),
            catchError(err => {
                this.updateData(type, []);
                return of([]);
            })
        ).subscribe();
    }

    /**
     * Documents achat pour un fournisseur : GET .../documents/fournisseur/:id/type/:type_document_id?page&limit&station_id
     * et statut_id en query seulement si un statut précis est choisi (pas pour « Tous »).
     */
    loadByTypeAndStatusForFournisseur(fournisseurId: number, type: string, status: string): void {
        this.loadUserData();
        if (!fournisseurId || fournisseurId <= 0) {
            this.updateData(type, [], true);
            return;
        }
        if (type === 'retour-fournisseur' && !this.retourFournisseurTypeDocumentId) {
            this.resolveRetourFournisseurTypeDocumentId().pipe(
                mergeMap((id) => {
                    if (!id) {
                        this.updateData('retour-fournisseur', [], true);
                        return EMPTY;
                    }
                    this.retourFournisseurTypeDocumentId = String(id);
                    this.loadByTypeAndStatusForFournisseur(fournisseurId, type, status);
                    return EMPTY;
                }),
                catchError(() => {
                    this.updateData('retour-fournisseur', [], true);
                    return EMPTY;
                })
            ).subscribe();
            return;
        }
        const typeId = this.getTypeIdForLoad(type);
        if (!typeId) {
            this.updateData(type, [], true);
            return;
        }

        if (status === 'Tous') {
            this.loadAllDocumentsForFournisseurByType(fournisseurId, type);
            return;
        }

        const statusId = this.statusMap[status] ?? '0';
        this.documentService
            .getDocumentsByFournisseurTypeStatut(fournisseurId, typeId, statusId, {
                page: 1,
                limit: 100,
                stationId: this.stationId
            })
            .pipe(
                tap(response => {
                    const docs = this.extractDocumentsFromFournisseurResponse(response);
                    if (type === 'facture-fournisseur') {
                        const totalItems =
                            response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                        this.factureFournisseurTotalItemsByStatus[status] = Number(totalItems) || 0;
                    }
                    if (type === 'retour-fournisseur') {
                        const totalItems =
                            response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                        this.retourFournisseurTotalItemsByStatus[status] = Number(totalItems) || 0;
                    }
                }),
                map(response =>
                    this.extractDocumentsFromFournisseurResponse(response).map((item: any) =>
                        this.normalizeItem(item, status)
                    )
                ),
                tap(data => this.updateData(type, data, true)),
                catchError((err) => {
                    console.error('[AchatService] loadByTypeAndStatusForFournisseur — erreur', type, err);
                    this.updateData(type, [], true);
                    return of([]);
                })
            )
            .subscribe();
    }

    private extractDocumentsFromFournisseurResponse(response: any): any[] {
        const raw = response?.data?.documents;
        if (Array.isArray(raw)) {
            return raw;
        }
        return this.documentService.getDocumentsArrayFromApiResponse(response);
    }

    /**
     * Liste fiche fournisseur : un seul GET avec fournisseur_id + type_document_id (statut_id omis).
     * Commande fournisseur (12), bon réception (13), facture fournisseur (14).
     */
    private loadAllDocumentsForFournisseurByType(fournisseurId: number, type: string) {
        const typeId = this.getTypeIdForLoad(type);
        if (!typeId) {
            this.updateData(type, [], true);
            return;
        }
        this.documentService
            .getDocumentsByFournisseurTypeStatut(fournisseurId, typeId, null, {
                page: 1,
                limit: 100,
                stationId: this.stationId
            })
            .pipe(
                tap(response => {
                    if (type === 'facture-fournisseur') {
                        const docs = this.extractDocumentsFromFournisseurResponse(response);
                        const totalItems =
                            response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                        this.factureFournisseurTotalItemsByStatus['Tous'] = Number(totalItems) || docs.length;
                    }
                    if (type === 'retour-fournisseur') {
                        const docs = this.extractDocumentsFromFournisseurResponse(response);
                        const totalItems =
                            response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                        this.retourFournisseurTotalItemsByStatus['Tous'] = Number(totalItems) || docs.length;
                    }
                }),
                map(response =>
                    this.extractDocumentsFromFournisseurResponse(response).map((item: any) =>
                        this.normalizeItem(item, item.statut?.titre || 'Tous')
                    )
                ),
                tap(data => this.updateData(type, data, true)),
                catchError((err) => {
                    console.error('[AchatService] loadAllDocumentsForFournisseurByType — erreur', type, err);
                    this.updateData(type, [], true);
                    return of([]);
                })
            )
            .subscribe();
    }

    private getStatusKeysForType(type: string): string[] {
        if (type === 'avoir-fournisseur') {
            return ['Brouillon', 'Non utilisé', 'Partiellement utilisé', 'Utilisé', 'Annulé'];
        }
        return Object.keys(this.statusMap).filter(s => s !== 'Tous');
    }

    private loadAllStatusesForType(type: string) {
        const statusKeys = this.getStatusKeysForType(type);
        const requests = statusKeys.map(statut => {
            const statusId = this.statusMap[statut];
            return this.fetchFromApiWithTotal(type, statusId, statut);
        });

        forkJoin(requests).subscribe({
            next: (results: { documents: any[]; total_items: number }[]) => {
                const allDocs = results.reduce((acc, r) => acc.concat(r.documents), [] as any[]);
                if (type === 'facture-fournisseur') {
                    let totalTous = 0;
                    results.forEach((r, i) => {
                        totalTous += r.total_items;
                        if (statusKeys[i]) this.factureFournisseurTotalItemsByStatus[statusKeys[i]] = r.total_items;
                    });
                    this.factureFournisseurTotalItemsByStatus['Tous'] = totalTous;
                }
                if (type === 'retour-fournisseur') {
                    let totalTous = 0;
                    results.forEach((r, i) => {
                        totalTous += r.total_items;
                        if (statusKeys[i]) this.retourFournisseurTotalItemsByStatus[statusKeys[i]] = r.total_items;
                    });
                    this.retourFournisseurTotalItemsByStatus['Tous'] = totalTous;
                }
                this.updateData(type, allDocs);
            },
            error: () => this.updateData(type, [])
        });
    }

    private fetchFromApi(type: string, statusId: string): Observable<any[]> {
        return this.fetchFromApiWithTotal(type, statusId, '').pipe(
            map(r => r.documents)
        );
    }

    private getTypeIdForLoad(type: string): string | null {
        if (type === 'avoir-fournisseur') {
            return this.avoirFournisseurTypeDocumentId;
        }
        if (type === 'retour-fournisseur') {
            return this.retourFournisseurTypeDocumentId;
        }
        return this.typeMap[type] || null;
    }

    private resolveAvoirFournisseurTypeDocumentId(): Observable<number | null> {
        return this.documentService.getTypeDocuments().pipe(
            map((response: any) => {
                const list = this.extractTypeDocumentsList(response);
                for (const row of list) {
                    const titreNorm = this.normalizeAvoirTypeTitre(row?.titre || row?.nom || '');
                    const id = Number(row?.id ?? row?.type_document_id);
                    if (!id || Number.isNaN(id)) {
                        continue;
                    }
                    if (titreNorm === 'AVOIR FOURNISSEUR' || (titreNorm.includes('AVOIR') && titreNorm.includes('FOURNISSEUR'))) {
                        return id;
                    }
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    private resolveRetourFournisseurTypeDocumentId(): Observable<number | null> {
        return this.documentService.getTypeDocuments().pipe(
            map((response: any) => {
                const list = this.extractTypeDocumentsList(response);
                for (const row of list) {
                    const titreNorm = this.normalizeAvoirTypeTitre(row?.titre || row?.nom || '');
                    const id = Number(row?.id ?? row?.type_document_id);
                    if (!id || Number.isNaN(id)) {
                        continue;
                    }
                    if (
                        titreNorm === 'RETOUR FOURNISSEUR' ||
                        (titreNorm.includes('RETOUR') && titreNorm.includes('FOURNISSEUR'))
                    ) {
                        return id;
                    }
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    private extractTypeDocumentsList(response: any): any[] {
        if (Array.isArray(response)) {
            return response;
        }
        if (response?.data?.types && Array.isArray(response.data.types)) {
            return response.data.types;
        }
        if (Array.isArray(response?.data)) {
            return response.data;
        }
        if (response?.types && Array.isArray(response.types)) {
            return response.types;
        }
        const raw = response?.data ?? response;
        if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray((raw as any).types)) {
            return (raw as any).types;
        }
        return [];
    }

    private normalizeAvoirTypeTitre(s: string | null | undefined): string {
        return (s || '')
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }

    private fetchFromApiWithTotal(type: string, statusId: string, statusName: string): Observable<{ documents: any[]; total_items: number }> {
        const typeId = this.getTypeIdForLoad(type);
        if (!typeId) {
            return of({ documents: [], total_items: 0 });
        }
        const params = new HttpParams()
            .set('page', '1')
            .set('limit', '100')
            .set('station_id', this.stationId.toString());
        const url = `${this.apiBase}/${statusId}/${typeId}`;
        const statusLabel = statusName || Object.keys(this.statusMap).find(k => this.statusMap[k] === statusId) || 'Inconnu';
        return this.http.get<any>(url, { params }).pipe(
            map(response => {
                const docs = response?.data?.documents || [];
                const totalItems = response?.data?.total_items ?? response?.data?.pagination?.total_items ?? docs.length;
                return {
                    documents: docs.map((item: any) => this.normalizeItem(item, statusLabel)),
                    total_items: Number(totalItems) || 0
                };
            }),
            catchError(() => of({ documents: [], total_items: 0 }))
        );
    }

    private resolveListReference(item: any): string {
        const typeTitre = String(item?.typeDocument?.titre || item?.type_document?.titre || '').toUpperCase().trim();
        let ref = String(item?.ref_externe || '').trim();
        if (ref && /^FAC(?=[-_]|$)/i.test(ref) && typeTitre.includes('AVOIR')) {
            const prefix = typeTitre.includes('FOURNISSEUR') ? 'AF' : 'AVR';
            ref = ref.replace(/^FAC/i, prefix);
        }
        return ref || (item?.id != null ? `REF-${item.id}` : '');
    }

    private normalizeItem(item: any, status: string): CommandeFournisseur {
        // Extraire le nom du fournisseur depuis différentes structures possibles
        let fournisseurName = '--';
        if (item.fornisseurs && Array.isArray(item.fornisseurs) && item.fornisseurs.length > 0) {
            // Structure avec "fornisseurs" (un seul 'n') comme dans l'API
            fournisseurName = item.fornisseurs[0]?.nom_entreprise || '--';
        } else if (item.fournisseurs && Array.isArray(item.fournisseurs) && item.fournisseurs.length > 0) {
            // Structure avec "fournisseurs" (deux 'n')
            fournisseurName = item.fournisseurs[0]?.nom_entreprise || '--';
        } else if (item.fournisseur_data?.nom_entreprise) {
            fournisseurName = item.fournisseur_data.nom_entreprise;
        } else if (item.fournisseur_data?.nom_fournisseur) {
            fournisseurName = item.fournisseur_data.nom_fournisseur;
        } else if (item.client_data?.nom_client) {
            fournisseurName = item.client_data.nom_client;
        }
        
        // Extraire le matricule/NIF depuis différentes structures
        let matricule = 'N/A';
        if (item.fornisseurs && Array.isArray(item.fornisseurs) && item.fornisseurs.length > 0) {
            matricule = item.fornisseurs[0]?.numero_identification_fiscale || 'N/A';
        } else if (item.fournisseurs && Array.isArray(item.fournisseurs) && item.fournisseurs.length > 0) {
            matricule = item.fournisseurs[0]?.numero_identification_fiscale || 'N/A';
        } else if (item.fournisseur_data?.numero_identification_fiscale) {
            matricule = item.fournisseur_data.numero_identification_fiscale;
        } else if (item.fournisseur_data?.matricule) {
            matricule = item.fournisseur_data.matricule;
        } else if (item.client_data?.matricule) {
            matricule = item.client_data.matricule;
        }
        
        // Extraire l'email depuis différentes structures
        let email: string | undefined = undefined;
        if (item.fornisseurs && Array.isArray(item.fornisseurs) && item.fornisseurs.length > 0) {
            email = item.fornisseurs[0]?.email;
        } else if (item.fournisseurs && Array.isArray(item.fournisseurs) && item.fournisseurs.length > 0) {
            email = item.fournisseurs[0]?.email;
        } else if (item.fournisseur_data?.email) {
            email = item.fournisseur_data.email;
        } else if (item.client_data?.email) {
            email = item.client_data.email;
        }
        
        // Extraire les téléphones depuis différentes structures
        let phoneNumbers: string[] = [];
        if (item.fornisseurs && Array.isArray(item.fornisseurs) && item.fornisseurs.length > 0) {
            phoneNumbers = item.fornisseurs[0]?.phone ? [item.fornisseurs[0].phone] : [];
        } else if (item.fournisseurs && Array.isArray(item.fournisseurs) && item.fournisseurs.length > 0) {
            phoneNumbers = item.fournisseurs[0]?.phone ? [item.fournisseurs[0].phone] : [];
        } else if (item.fournisseur_data?.telephones && Array.isArray(item.fournisseur_data.telephones)) {
            phoneNumbers = item.fournisseur_data.telephones;
        } else if (item.client_data?.telephones && Array.isArray(item.client_data.telephones)) {
            phoneNumbers = item.client_data.telephones;
        }
        
        return {
            id: item.id,
            reference: this.resolveListReference(item),
            clientName: fournisseurName,
            date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
            revenue: item.total_ttc ? `${parseFloat(item.total_ttc).toFixed(3)} TND` : '0.000 TND',
            matricule: matricule,
            status: item.statut?.titre || status,
            email: email,
            phoneNumbers: phoneNumbers,
            station_id: item.station_id || null,
            user_id: item.user_id || null,
            fullData: item
        };
    }

    /**
     * @param fromSupplierFournisseurApi si true, écrit toujours (API fiche fournisseur).
     *        Sinon, ignoré quand supplierDocumentScopeActive contient ce type (évite course avec loadAllData).
     */
    private updateData(type: string, data: CommandeFournisseur[], fromSupplierFournisseurApi = false) {
        if (!fromSupplierFournisseurApi && this.supplierDocumentScopeActive.has(type)) {
            return;
        }
        switch (type) {
            case 'commande-fournisseur':
                this.commandeFournisseurTableData.next(data);
                break;
            case 'bon-reception':
                this.bonReceptionTableData.next(data);
                break;
            case 'facture-fournisseur':
                this.factureFournisseurTableData.next(data);
                break;
            case 'retour-fournisseur':
                this.retourFournisseurTableData.next(data);
                break;
            case 'avoir-fournisseur':
                this.avoirFournisseurTableData.next(data);
                break;
        }
    }

    filterAvoirFournisseurByStatus(status: string) {
        this.loadByTypeAndStatus('avoir-fournisseur', status);
    }

    filterAvoirFournisseurByDateRange(start: string, end: string) {
        const data = this.avoirFournisseurTableData.getValue();
        const filtered = data.filter(item => {
            const d = new Date(item.date);
            return d >= new Date(start) && d <= new Date(end);
        });
        this.avoirFournisseurTableData.next(filtered);
    }
    filterCommandeFournisseurByStatus(status: string) {
        this.loadByTypeAndStatus('commande-fournisseur', status);
    }

    filterBonReceptionByStatus(status: string) {
        this.loadByTypeAndStatus('bon-reception', status);
    }

    filterFactureFournisseurByStatus(status: string) {
        this.loadByTypeAndStatus('facture-fournisseur', status);
    }

    filterRetourFournisseurByStatus(status: string) {
        this.loadByTypeAndStatus('retour-fournisseur', status);
    }

    filterCommandeFournisseurByDateRange(start: string, end: string) {
        const data = this.commandeFournisseurTableData.getValue();
        const filtered = data.filter(item => {
            const d = new Date(item.date);
            return d >= new Date(start) && d <= new Date(end);
        });
        this.commandeFournisseurTableData.next(filtered);
    }

    filterBonReceptionByDateRange(start: string, end: string) {
        const data = this.bonReceptionTableData.getValue();
        const filtered = data.filter(item => {
            const d = new Date(item.date);
            return d >= new Date(start) && d <= new Date(end);
        });
        this.bonReceptionTableData.next(filtered);
    }

    filterFactureFournisseurByDateRange(start: string, end: string) {
        const data = this.factureFournisseurTableData.getValue();
        const filtered = data.filter(item => {
            const d = new Date(item.date);
            return d >= new Date(start) && d <= new Date(end);
        });
        this.factureFournisseurTableData.next(filtered);
    }

    filterRetourFournisseurByDateRange(start: string, end: string) {
        const data = this.retourFournisseurTableData.getValue();
        const filtered = data.filter(item => {
            const d = new Date(item.date);
            return d >= new Date(start) && d <= new Date(end);
        });
        this.retourFournisseurTableData.next(filtered);
    }

    getCommandeFournisseurStatusCount(status: string): number {
        return this.getStatusCount(this.commandeFournisseurTableData.getValue(), status);
    }

    getBonReceptionStatusCount(status: string): number {
        return this.getStatusCount(this.bonReceptionTableData.getValue(), status);
    }

    getFactureFournisseurStatusCount(status: string): number {
        return this.getStatusCount(this.factureFournisseurTableData.getValue(), status);
    }

    /** Nombre total renvoyé par l'API pour la facture fournisseur (total_items). */
    getFactureFournisseurTotalItems(status: string): number {
        const n = this.factureFournisseurTotalItemsByStatus[status];
        return typeof n === 'number' ? n : 0;
    }

    /** Nombre total renvoyé par l'API pour le retour fournisseur (total_items). */
    getRetourFournisseurTotalItems(status: string): number {
        const n = this.retourFournisseurTotalItemsByStatus[status];
        return typeof n === 'number' ? n : 0;
    }

    private getStatusCount(data: CommandeFournisseur[], status: string): number {
        const normalized = this.normalizeStatus(status);
        return normalized === 'Tous' ? data.length : data.filter(i => i.status === normalized).length;
    }

    private normalizeStatus(status: string): string {
        const map: { [key: string]: string } = {
            tous: 'Tous', 
            brouillon: 'Brouillon', 
            cree: 'Créé', 
            valide: 'Valide',
            annule: 'Annulé', 
            recu: 'Reçu', 
            payé: 'Payé', 
            paye: 'Payé',
            non_payé: 'Non payé',
            'non payé': 'Non payé',
            'non paye': 'Non payé',
            'partiellement payé': 'Partiellement payé',
            'partiellement paye': 'Partiellement payé',
            partpaye: 'Partiellement payé',
            'part-paye': 'Partiellement payé'
        };
        return map[status.toLowerCase().trim()] || status;
    }
    getCommandeFournisseurTableData(): CommandeFournisseur[] {
        return this.commandeFournisseurTableData.getValue();
    }

    getBonReceptionTableData(): CommandeFournisseur[] {
        return this.bonReceptionTableData.getValue();
    }

    getFactureFournisseurTableData(): CommandeFournisseur[] {
        return this.factureFournisseurTableData.getValue();
    }

    getRetourFournisseurTableData(): CommandeFournisseur[] {
        return this.retourFournisseurTableData.getValue();
    }

    getAvoirFournisseurTableData(): CommandeFournisseur[] {
        return this.avoirFournisseurTableData.getValue();
    }
}
